import type { ReactNode } from 'react';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { Icon } from '../components/icons';

/**
 * A drag-to-reorder list for the small lists inside the properties panel — a matrix's rows
 * and columns, and a question's choices.
 *
 * Order is meaningful in every one of them: it is the order respondents read, and for a
 * matrix it is the shape of the grid. Until now the only way to change it was to delete
 * everything after the mistake and retype it.
 *
 * Its own DndContext rather than the Builder's. That one spans the palette and every page
 * and answers drops by moving elements between pages; a row inside a matrix has nothing to
 * do with any of that, and feeding both through one context would mean every canvas drop
 * handler had to first work out whether the thing being dragged was even an element.
 */
export function SortableRows({ count, onMove, children }: {
  count: number;

  /** Called with the indices to swap, already validated as in range and different. */
  onMove: (from: number, to: number) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    // Matches the canvas: a few pixels of movement before a drag starts, so clicking into
    // a text field in one of these rows does not begin dragging it.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = Number(active.id);
    const to = Number(over.id);

    if (Number.isNaN(from) || Number.isNaN(to)) return;
    if (from < 0 || to < 0 || from >= count || to >= count) return;

    onMove(from, to);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      {/* Positions are the identity here. These rows have no stable key of their own — a
          choice's value is editable and can be temporarily blank or duplicated while
          somebody is typing, which would collide or vanish mid-drag. */}
      <SortableContext
        items={Array.from({ length: count }, (_, i) => String(i))}
        strategy={verticalListSortingStrategy}
      >
        <ul className="space-y-1.5">{children}</ul>
      </SortableContext>
    </DndContext>
  );
}

/**
 * One row, with a handle.
 *
 * The handle is a real button rather than the whole row being draggable: the row is mostly
 * text inputs, and making those drag surfaces would break selecting text inside them.
 */
export function SortableRow({ index, label, children, footer }: {
  index: number;

  /** Announced to screen readers, e.g. "Reorder row 2". */
  label: string;
  children: ReactNode;

  /**
   * Rendered under the handle row, indented past it. Choices need it — their per-option
   * flags belong to the option above them, so they have to move with it when it is dragged.
   */
  footer?: ReactNode;
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: String(index) });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(
        'space-y-1 rounded-md',
        // Lifted rather than hidden while dragging, so the row being moved stays readable.
        isDragging && 'relative z-10 bg-[var(--ff-surface)] shadow-sm ring-1 ring-[var(--ff-border)]',
      )}
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label={label}
          title={label}
          className="shrink-0 cursor-grab touch-none rounded p-0.5 text-[var(--ff-text-faint)]
                     hover:text-[var(--ff-text)] active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <Icon name="drag" />
        </button>

        {children}
      </div>

      {/* Indented to clear the handle, so it reads as belonging to the row above it. */}
      {footer && <div className="pl-6">{footer}</div>}
    </li>
  );
}
